/**
 * The settings merge every consumer resolves with. Deliberately semantics-free:
 * this package supplies the operation, each app supplies the layers and knows
 * what the keys mean.
 *
 * Rules, in full:
 * - Plain objects deep-merge, later layers winning key by key.
 * - Arrays and primitives REPLACE. A tenant's `categories` list overwrites the
 *   default rather than appending to it.
 * - `null` CLEARS: it removes the key a lower layer set, which is the only way a
 *   deployment can drop something its preset declared (a mapping portal with no
 *   home page, say). `undefined` is inert and leaves the lower value standing.
 */
export function mergeSettings<T = Record<string, unknown>>(
	...layers: (unknown | undefined)[]
): T {
	let out: unknown = undefined
	for (const layer of layers) {
		if (layer === undefined) continue
		out = mergeTwo(out, layer)
	}
	return (out ?? {}) as T
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function mergeTwo(base: unknown, next: unknown): unknown {
	if (next === undefined) return base
	if (!isPlainObject(base) || !isPlainObject(next)) return clone(next)

	const out: Record<string, unknown> = { ...base }
	for (const [key, value] of Object.entries(next)) {
		if (value === null) {
			delete out[key]
			continue
		}
		if (value === undefined) continue
		out[key] = mergeTwo(out[key], value)
	}
	return out
}

function clone<T>(value: T): T {
	if (Array.isArray(value)) return value.map(clone) as unknown as T
	if (isPlainObject(value)) {
		const out: Record<string, unknown> = {}
		for (const [k, v] of Object.entries(value)) out[k] = clone(v)
		return out as T
	}
	return value
}
